/* eslint-disable no-param-reassign */
import { schemeYlGnBu } from "d3-scale-chromatic";
import { extent } from "d3-array";
import { scaleQuantize } from "d3-scale";
import lightenDarkenColor from "./dataHelpers/lightenDarkenColor";

const createColorScale = ({
  data,
  accessor,
  colorScheme = schemeYlGnBu,
  filterVals = undefined,
  cleanVals = undefined,
  colorSteps = undefined,
  bumpColorStep = false,
  unknownColor = "#e2e2e2",
  handleSmallAndIrregular = false,
}) => {
  /**
   * Removes outliers from data and builds a d3 quantize scale. Good for choropleth maps.
   *
   * @param data {arr<obj>} of objects. Data that will be used for the scale. Used to generate domain.
   * @param accessor {string} Target prop in data that will be used as the values for the scale.
   * @param colorScheme {arr<str>, optional} If provided, will be used as color scheme. Defaults to a d3 scale chromatic value.
   * @param bumpColorStep {bool} Whether to move color scale up one, which can create a slightly different look for the scale.
   * @param filterVals {func, optional} If provided will filter data before domain is created. eg. (val) => val > 0;
   * @param cleanVals {func, optional} If provided will apply this func to each value in data before domain is created. eg. (val) => val * 100;
   * @param colorSteps {int, optional} Number of steps in color scheme. Number of steps must be equal or greater to length of colorScheme array.
   * @param unknownColor {string, optional} Hex code to return for values that are not in domain.
   * @param handleSmallAndIrregular {bool, optional} Whether to remove small or irregular values.
   *
   * @return {func} Function that creates a color scale.
   */

  // SET COLOR RANGE
  let colorRange;
  if (!Array.isArray(colorScheme[0])) {
    // If array of array then assume this is a d3 color scheme

    if (bumpColorStep) {
      colorSteps += 1;
    }

    colorRange = colorScheme[colorSteps];

    if (bumpColorStep) {
      // Remove color range value at index 0
      colorRange = colorRange.filter((_, i) => i !== 0);
    }
  } else {
    // colorScheme is just an array, so assign
    colorRange = colorScheme;
  }

  // GET VALUES FOR DOMAIN
  let dataArr = data.map((item) => item[accessor]);

  // CLEAN VALUES
  if (cleanVals) {
    dataArr = dataArr.map(cleanVals);
  }

  // SPECIAL FILTER
  if (filterVals) {
    dataArr = dataArr.filter(filterVals);
  }

  let dataExtent = extent(dataArr);
  /**
   * HANDLE SMALL AND IRREGULAR VALUES
   * So that our legend doesn't look funny, we adjust the domain to better handle small values and to try
   * and round it to numbers that best fit the number of items in the range.
   */
  if (handleSmallAndIrregular) {
    const maxVal = dataExtent[1];
    const rangeLength = colorRange.length;
    if (maxVal < rangeLength + 1) {
      dataExtent = [dataExtent[0], rangeLength];
    } else {
      const floor = Math.ceil(maxVal / (rangeLength * 10)) * (rangeLength * 10);
      dataExtent = [dataExtent[0], floor];
    }
  }
  /**
   * BUILD SCALE
   * Using quantize scale, rather than quantile, because that seems to be a more accurate way to display data
   * with such an extreme distribution.
   */
  const colorScale = scaleQuantize()
    .domain(dataExtent)
    .range(colorRange)
    .unknown(unknownColor)
    .nice();

  return colorScale;
};

export default createColorScale;
